import type { Chat, LeadGrade } from '@/data/types';
import { isMessage, previewOf, useChats } from '@/data/store';
import { Avatar } from './Avatar';
import { ArchiveIcon, BellIcon, BellOffIcon, CashIcon, CheckIcon, MailIcon, MoreIcon, PersonIcon, ReadIcon, SparkIcon, StarIcon, TackIcon, Ticks, UnarchiveIcon, UnreadIcon } from './Icons';
import { useAuth } from '@/lib/auth';
import { Swipeable } from './Swipeable';
import { LabelLine } from './Labels';
import { AiLeo } from '@/components/AiLeo';
import { customerStatus } from '@/lib/chatGroups';
import { reminderFor, shortDate, useReminders } from '@/lib/reminders';

// One chat in a list, with WhatsApp's iPhone gestures: tap to open, long-press for its menu
// (Gary, 15 Oct; select mode is under the ⋯ at the top of the list).
// On a computer (a mouse, no swipes) right-click or the ⋮ that shows on hover opens the
// same More sheet, as WhatsApp's desktop app does (Gary, 15 Oct).
// Swipe right: Unread (or Read) and Pin. Swipe left: More and Archive. A full swipe runs
// the outer action: right marks read or unread, left archives.

export function needsReply(c: Chat): boolean {
  return c.dueMinutes !== undefined && !c.answered;
}

/**
 * How long the customer has been waiting, gently (Gary, 16 Oct: no countdown, no
 * "escalated"): "Waiting 6 min" within the hour, else "Waiting since 19:42", or with
 * the day when it was not today.
 */
/** Leo's hand-over reasons in plain words for the top line (Gary, 23 Oct: "reserve_time" showed as it is). */
const HANDOVER_WORDS: Record<string, string> = {
  money: 'an invoice or payment question', discount: 'a discount question', complaint: 'a complaint',
  asked_for_person: 'they asked for a person', someone_else: 'it needs someone else', cancel: 'a cancellation',
  late_change: 'a booking change', business: 'a business enquiry', licensing: 'licensing interest',
  reserve_time: 'they want a time confirmed', other: 'it needs a person',
};

export function handoverWords(reason: string | undefined): string {
  return reason ? HANDOVER_WORDS[reason] ?? reason : 'it needs a person';
}

export function waitingLine(c: Chat): string {
  if (!c.waitingSince) return 'Waiting for a reply';
  const since = new Date(c.waitingSince);
  const mins = Math.max(1, Math.round((Date.now() - since.getTime()) / 60_000));
  if (mins < 60) return `Waiting ${mins} min`;
  const time = since.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (since.toDateString() === new Date().toDateString()) return `Waiting since ${time}`;
  return `Waiting since ${since.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`;
}

/**
 * Who owns the chat (Gary, 21 Oct): always a person, never Leo. "You" on your own, "No owner" when nobody has it.
 * Nothing on Team chats.
 */
export function ownerLabel(c: Chat): string | null {
  if (c.isTeam) return null;
  if (c.takenOver) return 'You';
  if (c.owner) return c.owner.split(' ')[0] ?? c.owner;
  return 'No owner';
}

/** Who answers right now (Gary, 21 Oct): Leo, or the owner ("You", or head office when nobody owns it). */
export function dutyLabel(c: Chat): string | null {
  if (c.isTeam) return null;
  if (c.onDuty === 'leo' || (c.onDuty === undefined && c.aiState === 'ai')) return 'Leo';
  if (c.takenOver) return 'You';
  return c.owner ? (c.owner.split(' ')[0] ?? c.owner) : 'Head office';
}

/** Someone must act now (orange): the customer is waiting, or Leo handed over. */
export function actionLine(c: Chat): string | null {
  if (c.aiState === 'human_required') return `Leo handed over: ${handoverWords(c.aiHandover)}`;
  if (needsReply(c)) return waitingLine(c);
  return null;
}

/**
 * The customer's status as a coloured chip: Unpaid, New, or their loyalty tier (Gary, 18 Oct). A lead Leo has
 * graded shows its grade instead of New (Gary, 28 Oct); the New filter still finds it.
 */
export function StatusChip({ chat, large = false }: { chat: Chat; large?: boolean }) {
  const status = customerStatus(chat);
  if (!status) return null;
  if (status === 'New' && chat.grade && !chat.customerId) {
    return <span className={`chats-status chats-status--${chat.grade}${large ? ' chats-status--lg' : ''}`} title="Leo's lead grade">{GRADE_NAME[chat.grade]}</span>;
  }
  return (
    <span className={`chats-status chats-status--${status.toLowerCase()}${large ? ' chats-status--lg' : ''}`}>
      {status === 'Unpaid' ? <CashIcon /> : status === 'New' ? <StarIcon filled /> : null}{status}
    </span>
  );
}

/**
 * Who owns it, who is on duty, then the rest, as coloured tags (Gary, 18 Oct; owner and duty 21 Oct). The same
 * on a chat card and in the chat's header. Owner and duty always come first, in the same two places, so labels,
 * Email or Closed never push them along (Gary, 21 Oct): anything after them is cut off at the end instead.
 */
export function ChatTags({ chat: c, labels = true, status = true, people = true, old }: { chat: Chat; headOffice?: boolean; labels?: boolean; status?: boolean; people?: boolean; old?: string }) {
  // A chat's top line says the owner and who is on duty, so its header leaves those tags out (Gary, 23 Oct).
  const owner = people ? ownerLabel(c) : null;
  // Only Leo gets a duty chip (Gary, 23 Oct: "Head office on duty" was too long); a person on duty is the owner.
  const duty = people && dutyLabel(c) === 'Leo' ? 'Leo' : null;
  // The follow-up date, on the row and the chat's header (Gary, 3 Nov 2026).
  const { live } = useChats();
  const { reminders } = useReminders(live);
  const reminder = c.isTeam ? null : reminderFor(reminders, c);
  return (
    <>
      {owner && (
        <span className={`chats-row-who chats-row-who--${owner === 'No owner' ? 'none' : 'person'}`} title="Owner">
          <PersonIcon />{owner}
        </span>
      )}
      {duty && (
        <span className="chats-row-who chats-row-duty chats-row-who--leo" title="Leo is on duty">
          <AiLeo />
        </span>
      )}
      <span className="chats-row-extra">
        {old && <span className="chats-row-who chats-row-who--old">{old}</span>}
        {/* An email chat (Gary, 19 Oct: Leo on email): replies go by email, not WhatsApp. */}
        {(c.email || c.alsoOn === 'email') && <span className="chats-row-who chats-row-who--email"><MailIcon />Email</span>}
        {/* A Facebook or Instagram DM (Gary, 3 Nov 2026). */}
        {c.dm && <span className={`chats-row-who chats-row-who--${c.dm}`}>{c.dm === 'facebook' ? 'Facebook' : 'Instagram'}</span>}
        {/* Both channels in one row: WhatsApp named too, so the row says it holds two conversations. */}
        {c.alsoOn && <span className="chats-row-who chats-row-who--whatsapp">WhatsApp</span>}
        {/* Leo off for this customer, on every row and the chat's header (Gary, 20 Oct; 23 Oct: it was easy to miss). */}
        {reminder && <span className="chats-row-who chats-row-who--reminder" title={`Reminder: ${reminder.note ?? 'follow up'}`}><BellIcon />{shortDate(reminder.date)}</span>}
        {people && c.leoOff && <span className="chats-row-who chats-row-who--leo-off" title="Leo is off for this customer">Leo off</span>}
        {labels && <LabelLine labels={c.labels} inline />}
      </span>
      {status && <span className="chats-row-stage"><StatusChip chat={c} /></span>}
    </>
  );
}

function LastLine({ chat }: { chat: Chat }) {
  for (let i = chat.thread.length - 1; i >= 0; i -= 1) {
    const m = chat.thread[i];
    // A website enquiry's card reads as its first line (Gary, 17 Oct).
    if (m?.from === 'lead') return <span className="chats-row-msg-text">{m.text.split('\n')[0]}</span>;
    if (!m || !isMessage(m) || m.from === 'system' || m.from === 'note') continue;
    return (
      <>
        {m.from !== 'in' && <Ticks delivery={m.delivery} />}
        <span className="chats-row-msg-text">{previewOf(m.body)}</span>
      </>
    );
  }
  return null;
}

interface Props {
  chat: Chat;
  selecting: boolean;
  selected: boolean;
  showBrand: boolean;
  /** The chat open beside the list (desktop). */
  active?: boolean;
  /** Say when it is archived or closed: search lists old chats beside live ones (mobile review, 19 Oct). */
  /** Tag old chats: true tags closed and archived ones (search), 'closed' only closed ones (Archived, where all are archived). */
  markOld?: boolean | 'closed';
  onOpen: () => void;
  onToggleSelect: () => void;
  onArchive: () => void;
  onToggleRead: () => void;
  onTogglePin: () => void;
  onMore: () => void;
}

export function ChatRow({ chat: c, selecting, selected, showBrand, active, markOld, onOpen, onToggleSelect, onArchive, onToggleRead, onTogglePin, onMore }: Props) {
  const headOffice = useAuth().access.role === 'head_office';
  const unread = c.unread > 0 || c.markedUnread;
  // One fixed place per group (Gary, 18 Oct): who has it and the stage on the bottom row; a card that
  // needs someone is shaded, and the reason is in the chat.
  const action = actionLine(c);
  return (
    <Swipeable
      // Peace of Sleep rows in gold, CleanMyBed rows plain (Gary, 3 Nov 2026); Leo on duty is his spark on the picture.
      className={`chats-row--brand${c.brand === 'pos' ? ' chats-brand-pos' : ''}${selected ? ' chats-row--selected' : ''}${active ? ' chats-row--active' : ''}`}
      ariaLabel={`${c.name}${c.unread ? `, ${c.unread} unread` : ''}`}
      onTap={selecting ? onToggleSelect : onOpen}
      onLongPress={selecting ? onToggleSelect : onMore}
      left={selecting ? undefined : [
        { label: c.archived ? 'Unarchive' : 'Archive', icon: c.archived ? <UnarchiveIcon /> : <ArchiveIcon />, tone: 'primary', run: onArchive },
        { label: 'More', icon: <MoreIcon />, tone: 'grey', run: onMore },
      ]}
      right={selecting ? undefined : [
        { label: unread ? 'Read' : 'Unread', icon: unread ? <ReadIcon /> : <UnreadIcon />, tone: 'primary', run: onToggleRead },
        ...(c.archived ? [] : [{ label: c.pinned ? 'Unpin' : 'Pin', icon: <TackIcon />, tone: 'grey' as const, run: onTogglePin }]),
      ]}
    >
      <div
        className="chats-row"
        title={action ?? undefined}
        onContextMenu={(e) => {
          // A mouse only: on a phone the long press opens the same menu.
          if (selecting || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          e.preventDefault();
          e.stopPropagation(); // not also the row's long-press (select)
          onMore();
        }}
      >
        <span className="chats-row-avatar">
          <Avatar name={c.name} colour={c.colour} email={!!c.email || c.alsoOn === 'email'} />
          {/* Only Peace of Sleep chats carry the dot: nearly every chat is CleanMyBed (Gary, 18 Oct). */}
          {showBrand && c.brand === 'pos' && <span className={`chats-row-brand chats-row-brand--${c.brand}`} aria-label={c.brand === 'pos' ? 'Peace of Sleep' : 'CleanMyBed'} />}
          {dutyLabel(c) === 'Leo' && <span className="chats-row-leo" role="img" aria-label="Leo on duty"><SparkIcon /></span>}
          {selected && <span className="chats-row-check" aria-hidden="true"><CheckIcon /></span>}
        </span>
        <div className="chats-row-main">
          {/* Three fixed rows (Gary, 18 Oct): name with pin, mute and time; last message with the count;
              who, labels and stage. */}
          <div className="chats-row-top">
            <span className="chats-row-name">{c.name}</span>
            {c.pinned && <span className="chats-row-flag" aria-label="Pinned"><TackIcon /></span>}
            <span className={`chats-row-time${action ? ' chats-row-time--action' : unread ? ' chats-row-time--hot' : ''}`}>{c.time}</span>
            {/* Compact has no message row, so the count sits beside the time instead. */}
            {c.muted && <span className="chats-row-flag chats-row-count-compact" aria-label="Muted"><BellOffIcon /></span>}
            {c.unread > 0 && <span className={`chats-badge chats-badge--sm chats-row-count-compact${c.muted ? ' chats-badge--muted' : ''}`}>{c.unread}</span>}
            {c.unread === 0 && c.markedUnread && <span className="chats-badge chats-badge--dot chats-row-count-compact" aria-hidden="true" />}
          </div>
          <div className="chats-row-bottom">
            <span className="chats-row-msg"><LastLine chat={c} /></span>
            {/* Muted sits beside the count it greys out (Gary, 18 Oct). */}
            {c.muted && <span className="chats-row-flag" aria-label="Muted"><BellOffIcon /></span>}
            {c.unread > 0 && <span className={`chats-badge${c.muted ? ' chats-badge--muted' : ''}`}>{c.unread}</span>}
            {c.unread === 0 && c.markedUnread && <span className="chats-badge chats-badge--dot" aria-label="Marked unread" />}
            {!selecting && (
              <button
                type="button"
                className="chats-row-more"
                aria-label={`More for ${c.name}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onMore(); }}
              >
                <MoreIcon />
              </button>
            )}
          </div>
          {/* Always drawn, empty or not, so every card is the same height. */}
          <div className="chats-row-status">
            <ChatTags chat={c} headOffice={headOffice} old={markOld && (c.closed || (markOld === true && c.archived)) ? (c.closed ? 'Closed' : 'Archived') : undefined} />
          </div>
        </div>
      </div>
    </Swipeable>
  );
}

const GRADE_NAME: Record<LeadGrade, string> = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };
